import {
  Box,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import ErrorArrowUpIcon from "../assets/icons/icon-arrow-down-error.png";
import SuccessArrowDownIcon from "../assets/icons/icon-arrow-up-success.png";
import { findMeasureByNameAndDataSetId, getMeasuresList } from '../config/services/measures';
import { getReportById } from '../config/services/reports';
import { useReportDetails } from '../layouts/report/ReportDetailsContext';
import { CustomTooltip, FormatToShortNumber, formatCurrency, formatNumberTimeField, handleDataFormat } from '../utils/Reusables';
import { getRateChangeQueryData } from '../utils/getCubeData';
import NumberGraphicCard from './charts/NumberGraphicCard';

const NO_RATE_CHANGE_SVG = <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 250.52 208.444">
  <path id="Union_3" data-name="Union 3" d="M153.5,196.426v-33.2H9.5a9.5,9.5,0,1,1,0-19h144v-32.2a11.975,11.975,0,0,1,18-10.39l73.02,42.2a12,12,0,0,1,0,20.779l-73.02,42.2a11.975,11.975,0,0,1-18-10.39Zm-74.485-89.61L6,64.612A12,12,0,0,1,6,43.833l73.02-42.2a12,12,0,0,1,18,10.39v33.2h144a9.5,9.5,0,1,1,0,19h-144v32.2a11.976,11.976,0,0,1-18,10.39Z" transform="translate(0 0)" />
</svg>

export function NumberChartRenderer({ resultSet, fieldFormat, settings, columnTitle, columnDisplayName, noDataText, chartType, fieldsKeyInfo, reportName, description, rateChangeQuery, reportHyperlink, setHyperlinkType, setHyperlinkModal, setHyperlinkReportData, setHyperlinkExtUrl, setExtReportType, setUserInfo, query, ...rest }) {
  const { reportDetails } = useReportDetails();
  const [rateChangeState, setRateChangeState] = useState(null);
  const showYearOnYearChange = settings?.rateChangeProps?.showYearOnYearChange || false;

  //console.log(rest.query)

  useEffect(async () => {
    if (settings?.rateChangeProps && (settings?.rateChangeProps?.periodicity || settings?.rateChangeProps?.customVal)) {
      let prevValues = []
      let currValues = []
      let prevYearValue = [];
      let absValue = []
      let isNoChange = false
      let isPrevYearNoChange = false
      let isPercent = false
      let rate;
      let previousYearRate;
      let rateChangeResultSet;

      rateChangeResultSet = await getRateChangeQueryData(rateChangeQuery.query);
      await rateChangeResultSet?.series().forEach((s, index) => {
        if (index === 0) {
          prevValues = [...prevValues, ...s.series.map(obj => obj?.value || 0)]
          if (s?.key && fieldFormat?.[s?.key] === 'percent') {
            isPercent = true
          }
        }
        else if (index === 1) {
          currValues = [...currValues, ...s.series.map(obj => obj?.value || 0)]
          if (s?.key && fieldFormat?.[s?.key] === 'percent') {
            isPercent = true
          }
        }
        else if(index === 2 && showYearOnYearChange) {
          prevYearValue = [...prevYearValue, ...s.series.map(obj => obj?.value || 0)]
          if (s?.key && fieldFormat?.[s?.key] === 'percent') {
            isPercent = true
          }
        }
      });
      await resultSet.series().forEach(s => {
        absValue = [...absValue, ...s.series.map(obj => obj?.value || 0)]
        if (s?.key && fieldFormat?.[s?.key] === 'percent') {
          isPercent = true
        }
      })

      let totalNumPrev = Number(prevValues.reduce((a, b) => a + b, 0))
      let totalNumCurr = Number(currValues.reduce((a, b) => a + b, 0))
      let totalAbsValue = Number(absValue?.reduce((a, b) => a + b, 0))
      let difference = Number(totalNumCurr) - Number(totalNumPrev);
      if (totalNumPrev === totalNumCurr) {
        rate = 0
      }
      else if (totalNumPrev === 0) {
        rate = 100
        difference = 0
      }
      else if ((totalNumCurr > totalNumPrev) || (totalNumCurr < totalNumPrev) || totalNumCurr === 0) {
        rate = parseFloat(Math.abs(Math.abs(difference) / Number(totalNumPrev))) * 100

        if (rate && String(rate)?.split('.')?.length > 1) {
          rate = rate?.toFixed(2)

        }
      }
      if (rate === 0) {
        isNoChange = true
      }

      let prevYearNumberTrend;

      if (showYearOnYearChange) {
        let totalNumPrevYear = Number(prevYearValue.reduce((a, b) => a + b, 0))
        if (totalNumPrevYear === totalNumCurr) {
          previousYearRate = 0
        }
        else if (totalNumPrevYear === 0) {
          previousYearRate = 100
        }
        else if ((totalNumCurr > totalNumPrevYear) || (totalNumCurr < totalNumPrevYear) || totalNumCurr === 0) {
          previousYearRate = parseFloat(Math.abs(Math.abs(totalNumCurr - totalNumPrevYear) / Number(totalNumPrevYear))) * 100
          if (previousYearRate && String(previousYearRate)?.split('.')?.length > 1) {
            previousYearRate = previousYearRate?.toFixed(2)
          }
        }
        if (previousYearRate === 0) {
          isPrevYearNoChange = true
        }
        prevYearNumberTrend = totalNumPrevYear > totalNumCurr ? 'down' : 'up'
      }

      let numberTrend = difference > 0 ? 'up' : 'down';
      let currNum = `${handleDataFormat(settings, totalAbsValue)}${isPercent ? ' %' : ''}`;
      if (setRateChangeState)
        setRateChangeState({ isNoChange, isPrevYearNoChange, rate, previousYearRate, numberTrend,prevYearNumberTrend, currNum, rateChangeProps: settings?.rateChangeProps })
    }
  }, [rateChangeQuery, settings?.rateChangeProps]);


  if (!resultSet) return null;

  if (chartType === 'numberGraphicCard' || settings?.numberChartTemplate?.template === 'template4') {
    return <NumberGraphicCard {...{ reportName, description, resultSet, fieldFormat, settings, columnTitle, columnDisplayName, noDataText, chartType, query,...rest }} />
  }

  const calculatedNumber = getCalculateNumber({ resultSet, fieldFormat, settings, noDataText, query });

  if (settings?.numberChartTemplate?.template === 'template2') {
    let title = calculatedNumber;
    const hyperlink = reportHyperlink?.filter(link => link?.hyperlinkColumn === fieldsKeyInfo?.measureKey?.[0])?.[0];
    if (hyperlink && hyperlink?.hyperlinkColumn !== '') {
      if (hyperlink?.hyperlinkType === 'int') {
        title = <a
          style={{ cursor: 'pointer',textDecoration:'auto' }}
          onClick={
            (e) => {
              e.preventDefault();
              setHyperlinkType(hyperlink?.hyperlinkType)
              setHyperlinkModal(true);
              handleReportHyperlink(hyperlink, { [fieldsKeyInfo?.measureKey?.[0]]: title }, setHyperlinkReportData, query)
            }
          }
        >{title}</a>
      }
      if (hyperlink?.hyperlinkType === 'ext') {
        const target = hyperlink?.hyperLinkExtWindow === "new_window" ? "_blank" : hyperlink?.hyperLinkExtWindow === "same_window" ? "_parent" : undefined;
        let href = hyperlink?.hyperlinkExtType === "params" ? 
                        handleExternalReportHyperlink(query, { [fieldsKeyInfo?.measureKey?.[0]]: title }, hyperlink?.hyperlinkExtParams, hyperlink?.hyperlinkExtUrl, hyperlink?.hyperlinkExtType, setHyperlinkExtUrl, setHyperlinkModal, setUserInfo, setExtReportType,hyperlink?.hyperLinkExtWindow) 
                        :
                        hyperlink?.hyperlinkExtType === "qryParams" ?
                        handleExternalReportHyperlink(query, { [fieldsKeyInfo?.measureKey?.[0]]: title }, hyperlink?.hyperlinkExtQryParams, hyperlink?.hyperlinkExtUrl, hyperlink?.hyperlinkExtType, setHyperlinkExtUrl, setHyperlinkModal, setUserInfo, setExtReportType,hyperlink?.hyperLinkExtWindow)
                        :
                        hyperlink?.hyperlinkExtUrl

        title = target ? <a
          style={{ cursor: 'pointer',textDecoration:'auto' }}
          target={target}
          href={href}
        >{title}</a>
        :
        <a
            style={{ cursor: 'pointer' }}
            onClick={
              (e) => {
                e.preventDefault();
                setHyperlinkType(hyperlink?.hyperlinkType)
                if (hyperlink?.hyperlinkExtType === "params") {
                  handleExternalReportHyperlink(query, { [fieldsKeyInfo?.measureKey?.[0]]: title }, hyperlink?.hyperlinkExtParams, hyperlink?.hyperlinkExtUrl, hyperlink?.hyperlinkExtType, setHyperlinkExtUrl, setHyperlinkModal, setUserInfo, setExtReportType)
                }
                else if (hyperlink?.hyperlinkExtType === "qryParams") {
                  handleExternalReportHyperlink(query, { [fieldsKeyInfo?.measureKey?.[0]]: title }, hyperlink?.hyperlinkExtQryParams, hyperlink?.hyperlinkExtUrl, hyperlink?.hyperlinkExtType, setHyperlinkExtUrl, setHyperlinkModal, setUserInfo, setExtReportType)
                }
                else {
                  setHyperlinkModal(true);
                  setHyperlinkExtUrl(hyperlink?.hyperlinkExtUrl)
                }
  
              }}
  
          >
            {title}
          </a>
      }
      if (hyperlink?.hyperlinkType === 'dashboard') {
        const url = new URL(`${window.location.origin}/embedDashboard/${hyperlink?.hyperlinkDashboardId?._id}`);
        const hyperlinkColumnsForDashboard = hyperlink?.hyperlinkColumnsForDashboard?.map(item => { return { [item.split('.')[1]]: title } });
        if (hyperlinkColumnsForDashboard?.length > 0) {
          url.searchParams.append('filterParams', encodeURIComponent(JSON.stringify(hyperlinkColumnsForDashboard)));
        }
        title = <a href={url.href} style={{ cursor: 'pointer',textDecoration:'auto' }} target='_blank' >{title}</a>
      }
    }
    const icon = settings?.numberChartTemplate?.config?.icon;
    const color = settings?.numberChartTemplate?.config?.color;
    const reportDisplayName = () => {
      const columnDisplayNameKeyValue = Object.entries(columnDisplayName)[0];
      const columnTitleKeyValue = Object.entries(columnTitle[0])[0];
      if (columnTitleKeyValue?.[0] === columnDisplayNameKeyValue?.[0] && columnTitleKeyValue?.[1]?.length) {
        return columnTitleKeyValue[1]
      }
      if (columnDisplayNameKeyValue?.[0]) {
        return columnDisplayNameKeyValue[1]
      }
      if (!columnDisplayNameKeyValue?.[0] && !columnTitleKeyValue?.[0]) {
        return fieldsKeyInfo?.measureKey?.[0]?.split('.')[1]
      }
      return ''
    };
    return <ArrowNumberChart icon={icon} color={color} title={title} reportDisplayName={reportDisplayName()} rateChangeState={rateChangeState} isTitleNoDataText={title === noDataText} />
  }

  if (settings?.numberChartTemplate?.template === 'template3') {
    let subtitle = calculatedNumber;
    const hyperlink = reportHyperlink?.filter(link => link?.hyperlinkColumn === fieldsKeyInfo?.measureKey?.[0])?.[0];
    if (hyperlink && hyperlink?.hyperlinkColumn !== '') {
      if (hyperlink?.hyperlinkType === 'int') {
        subtitle = <a
          style={{ cursor: 'pointer',textDecoration:'auto' }}
          onClick={
            (e) => {
              e.preventDefault();
              setHyperlinkType(hyperlink?.hyperlinkType)
              setHyperlinkModal(true);
              handleReportHyperlink(hyperlink, { [fieldsKeyInfo?.measureKey?.[0]]: subtitle }, setHyperlinkReportData, query)
            }
          }
        >{subtitle}</a>
      }
      if (hyperlink?.hyperlinkType === 'ext') {
        const target = hyperlink?.hyperLinkExtWindow === "new_window" ? "_blank" : hyperlink?.hyperLinkExtWindow === "same_window" ? "_parent" : undefined;
        let href = hyperlink?.hyperlinkExtType === "params" ? 
                        handleExternalReportHyperlink(query, { [fieldsKeyInfo?.measureKey?.[0]]: subtitle }, hyperlink?.hyperlinkExtParams, hyperlink?.hyperlinkExtUrl, hyperlink?.hyperlinkExtType, setHyperlinkExtUrl, setHyperlinkModal, setUserInfo, setExtReportType,hyperlink?.hyperLinkExtWindow) 
                        :
                        hyperlink?.hyperlinkExtType === "qryParams" ?
                        handleExternalReportHyperlink(query, { [fieldsKeyInfo?.measureKey?.[0]]: subtitle }, hyperlink?.hyperlinkExtQryParams, hyperlink?.hyperlinkExtUrl, hyperlink?.hyperlinkExtType, setHyperlinkExtUrl, setHyperlinkModal, setUserInfo, setExtReportType,hyperlink?.hyperLinkExtWindow)
                        :
                        hyperlink?.hyperlinkExtUrl

        subtitle = target ? <a
          style={{ cursor: 'pointer',textDecoration:'auto' }}
          target={target}
          href={href}
        >{subtitle}</a>
        :
        <a
            style={{ cursor: 'pointer' }}
            onClick={
              (e) => {
                e.preventDefault();
                setHyperlinkType(hyperlink?.hyperlinkType)
                if (hyperlink?.hyperlinkExtType === "params") {
                  handleExternalReportHyperlink(query, { [fieldsKeyInfo?.measureKey?.[0]]: subtitle }, hyperlink?.hyperlinkExtParams, hyperlink?.hyperlinkExtUrl, hyperlink?.hyperlinkExtType, setHyperlinkExtUrl, setHyperlinkModal, setUserInfo, setExtReportType)
                }
                else if (hyperlink?.hyperlinkExtType === "qryParams") {
                  handleExternalReportHyperlink(query, { [fieldsKeyInfo?.measureKey?.[0]]: subtitle }, hyperlink?.hyperlinkExtQryParams, hyperlink?.hyperlinkExtUrl, hyperlink?.hyperlinkExtType, setHyperlinkExtUrl, setHyperlinkModal, setUserInfo, setExtReportType)
                }
                else {
                  setHyperlinkModal(true);
                  setHyperlinkExtUrl(hyperlink?.hyperlinkExtUrl)
                }
  
              }}
  
          >
            {subtitle}
          </a>
      }
      if (hyperlink?.hyperlinkType === 'dashboard') {
        const url = new URL(`${window.location.origin}/embedDashboard/${hyperlink?.hyperlinkDashboardId?._id}`);
        const hyperlinkColumnsForDashboard = hyperlink?.hyperlinkColumnsForDashboard?.map(item => { return { [item.split('.')[1]]: subtitle } });
        if (hyperlinkColumnsForDashboard?.length > 0) {
          url.searchParams.append('filterParams', encodeURIComponent(JSON.stringify(hyperlinkColumnsForDashboard)));
        }
        subtitle = <a href={url.href} style={{ cursor: 'pointer',textDecoration:'auto' }} target='_blank' >{subtitle}</a>
      }
    }
    const icon = settings?.numberChartTemplate?.config?.icon;
    const backgroundImage = settings?.numberChartTemplate?.config?.backgroundImage;
    const title = Object.values(columnTitle[0])[0] || Object.values(columnDisplayName)[0] || "";
    return <BackgroundImageNumberChart icon={icon} backgroundImage={backgroundImage} backgroundColor={settings?.numberChartTemplate?.config?.color} title={title} subTitle={subtitle} rateChangeState={rateChangeState} />
  }

  if (settings?.numberChartTemplate?.template === 'template5') {
    const topicCount = calculatedNumber || 0;
    const topicName = Object.values(columnTitle[0])[0] || Object.values(columnDisplayName)[0] || ""
    let topic = `(${topicCount} ${topicName})`
    const hyperlink = reportHyperlink?.filter(link => link?.hyperlinkColumn === fieldsKeyInfo?.measureKey?.[0])?.[0];
    if (hyperlink && hyperlink?.hyperlinkColumn !== '') {
      if (hyperlink?.hyperlinkType === 'int') {
        topic = <p
          style={{ cursor: 'pointer' }}
          onClick={
            (e) => {
              e.preventDefault();
              setHyperlinkType(hyperlink?.hyperlinkType)
              setHyperlinkModal(true);
              handleReportHyperlink(hyperlink, { [fieldsKeyInfo?.measureKey?.[0]]: topicCount }, setHyperlinkReportData, query)
            }
          }
        >{topic}</p>
      }
      if (hyperlink?.hyperlinkType === 'ext') {
        topic = <p
          style={{ cursor: 'pointer' }}
          onClick={
            (e) => {
              e.preventDefault();
              setHyperlinkType(hyperlink?.hyperlinkType)
              if (hyperlink?.hyperlinkExtType === "params") {
                handleExternalReportHyperlink(query, { [fieldsKeyInfo?.measureKey?.[0]]: topicCount }, hyperlink?.hyperlinkExtParams, hyperlink?.hyperlinkExtUrl, hyperlink?.hyperlinkExtType, setHyperlinkExtUrl, setHyperlinkModal, setUserInfo, setExtReportType)
              }
              else if (hyperlink?.hyperlinkExtType === "qryParams") {
                handleExternalReportHyperlink(query, { [fieldsKeyInfo?.measureKey?.[0]]: topicCount }, hyperlink?.hyperlinkExtQryParams, hyperlink?.hyperlinkExtUrl, hyperlink?.hyperlinkExtType, setHyperlinkExtUrl, setHyperlinkModal, setUserInfo, setExtReportType)
              }
              else {
                setHyperlinkModal(true);
                setHyperlinkExtUrl(hyperlink?.hyperlinkExtUrl)
              }

            }}
        >{topic}</p>
      }
      if (hyperlink?.hyperlinkType === 'dashboard') {
        const url = new URL(`${window.location.origin}/embedDashboard/${hyperlink?.hyperlinkDashboardId?._id}`);
        const hyperlinkColumnsForDashboard = hyperlink?.hyperlinkColumnsForDashboard?.map(item => { return { [item.split('.')[1]]: topic } });
        if (hyperlinkColumnsForDashboard?.length > 0) {
          url.searchParams.append('filterParams', encodeURIComponent(JSON.stringify(hyperlinkColumnsForDashboard)));
        }
        topic = <a href={url.href} style={{ cursor: 'pointer' }} target='_blank' >{topic}</a>
      }
    }
    const icon = settings?.numberChartTemplate?.config?.icon;
    const title = settings?.numberChartTemplate?.config?.title;
    const description = settings?.numberChartTemplate?.config?.description;
    return <StrogWeakNumberChart title={title} description={description} topic={topic} icon={icon} />
  }

  let formattedNum = calculatedNumber
  const hyperlink = reportHyperlink?.filter(link => link?.hyperlinkColumn === fieldsKeyInfo?.measureKey?.[0])?.[0];
    if (hyperlink && hyperlink?.hyperlinkColumn !== '') {
      if (hyperlink?.hyperlinkType === 'int') {
        formattedNum = <a
          style={{ cursor: 'pointer',textDecoration:'auto' }}
          onClick={
            (e) => {
              e.preventDefault();
              setHyperlinkType(hyperlink?.hyperlinkType)
              setHyperlinkModal(true);
              handleReportHyperlink(hyperlink, { [fieldsKeyInfo?.measureKey?.[0]]: formattedNum }, setHyperlinkReportData, query)
            }
          }
        >{formattedNum}</a>
      }
      if (hyperlink?.hyperlinkType === 'ext') {
        const target = hyperlink?.hyperLinkExtWindow === "new_window" ? "_blank" : hyperlink?.hyperLinkExtWindow === "same_window" ? "_parent" : undefined;
        let href = hyperlink?.hyperlinkExtType === "params" ? 
                        handleExternalReportHyperlink(query, { [fieldsKeyInfo?.measureKey?.[0]]: formattedNum }, hyperlink?.hyperlinkExtParams, hyperlink?.hyperlinkExtUrl, hyperlink?.hyperlinkExtType, setHyperlinkExtUrl, setHyperlinkModal, setUserInfo, setExtReportType,hyperlink?.hyperLinkExtWindow) 
                        :
                        hyperlink?.hyperlinkExtType === "qryParams" ?
                        handleExternalReportHyperlink(query, { [fieldsKeyInfo?.measureKey?.[0]]: formattedNum }, hyperlink?.hyperlinkExtQryParams, hyperlink?.hyperlinkExtUrl, hyperlink?.hyperlinkExtType, setHyperlinkExtUrl, setHyperlinkModal, setUserInfo, setExtReportType,hyperlink?.hyperLinkExtWindow)
                        :
                        hyperlink?.hyperlinkExtUrl

        formattedNum = target ? <a
          style={{ cursor: 'pointer',textDecoration:'auto' }}
          target={target}
          href={href}
        >{formattedNum}</a>
        :
        <a
            style={{ cursor: 'pointer' }}
            onClick={
              (e) => {
                e.preventDefault();
                setHyperlinkType(hyperlink?.hyperlinkType)
                if (hyperlink?.hyperlinkExtType === "params") {
                  handleExternalReportHyperlink(query, { [fieldsKeyInfo?.measureKey?.[0]]: formattedNum }, hyperlink?.hyperlinkExtParams, hyperlink?.hyperlinkExtUrl, hyperlink?.hyperlinkExtType, setHyperlinkExtUrl, setHyperlinkModal, setUserInfo, setExtReportType)
                }
                else if (hyperlink?.hyperlinkExtType === "qryParams") {
                  handleExternalReportHyperlink(query, { [fieldsKeyInfo?.measureKey?.[0]]: formattedNum }, hyperlink?.hyperlinkExtQryParams, hyperlink?.hyperlinkExtUrl, hyperlink?.hyperlinkExtType, setHyperlinkExtUrl, setHyperlinkModal, setUserInfo, setExtReportType)
                }
                else {
                  setHyperlinkModal(true);
                  setHyperlinkExtUrl(hyperlink?.hyperlinkExtUrl)
                }
  
              }}
  
          >
            {formattedNum}
          </a>
      }
      if (hyperlink?.hyperlinkType === 'dashboard') {
        const url = new URL(`${window.location.origin}/embedDashboard/${hyperlink?.hyperlinkDashboardId?._id}`);
        const hyperlinkColumnsForDashboard = hyperlink?.hyperlinkColumnsForDashboard?.map(item => { return { [item.split('.')[1]]: formattedNum } });
        if (hyperlinkColumnsForDashboard?.length > 0) {
          url.searchParams.append('filterParams', encodeURIComponent(JSON.stringify(hyperlinkColumnsForDashboard)));
        }
        formattedNum = <a href={url.href} style={{ cursor: 'pointer',textDecoration:'auto' }} target='_blank' >{formattedNum}</a>
      }
    }

  return (
    <Box
      className="report-number-graph"
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        width: "fit-content",
        height: "fit-content",
        // margin: 'auto' /* removed as content is center aligned by default */
      }}
    >
      {!reportDetails?.carouselName ? (
        <Typography
          variant="h6"
          style={{
            color: "#202124",
            fontSize: "17px",
            fontWeight: "400",
            textAlign: "left",
          }}
        >
          {reportName}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: "flex",
          alignItems: "start",
          justifyContent: "start",
          flexDirection: "column",
        }}
      >
        <Typography variant="h4" style={{ textAlign: "center" , padding:"10px 0px 0px 10px"}}>
          {formattedNum}
        </Typography>
        {rateChangeState ? (
          <Box 
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              marginTop: "10px",
              marginBottom: "10px",
              width: "fit-content",
              height: "fit-content",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                marginLeft: "5px",
                marginTop: "2px",
              }}
            >
              <Box
                sx={{
                  color:
                    rateChangeState?.numberTrend === "up"
                      ? "#34A853"
                      : "#EA4335",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {rateChangeState?.isNoChange ? (
                  NO_RATE_CHANGE_SVG
                ) : (
                  <>
                    {rateChangeState.numberTrend === "up" ? (
                      <img
                        src={SuccessArrowDownIcon}
                        className="cr-km-card-trend-icon "
                        alt=""
                        style={{ marginRight: "5px" }}
                      />
                    ) : (
                      <img
                        src={ErrorArrowUpIcon}
                        className="cr-km-card-trend-icon "
                        alt=""
                        style={{ marginRight: "5px" }}
                      />
                    )}
                    <Typography variant="h5">
                      {rateChangeState?.rate}%
                    </Typography>
                  </>
                )}
              </Box>
              <p style={{ fontSize: "12px", fontWeight: "600" }}>
                {rateChangeState?.rateChangeProps?.periodicity}
              </p>
            </Box>
            {showYearOnYearChange && (
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  marginLeft: "20px",
                  marginRight: "20px",
                  borderColor: "#b2b2b2",
                  alignSelf: "center",
                  height: "40px",
                  marginTop: "2px",
                }}
              />
            )}
            {showYearOnYearChange && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  marginLeft: "5px",
                  marginTop: "2px",
                }}
              >
                <Box
                  sx={{
                    color:
                      rateChangeState?.prevYearNumberTrend === "up"
                        ? "#34A853"
                        : "#EA4335",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {rateChangeState?.isPrevYearNoChange ? (
                    NO_RATE_CHANGE_SVG
                  ) : (
                    <>
                      {rateChangeState.prevYearNumberTrend === "up" ? (
                        <img
                          src={SuccessArrowDownIcon}
                          className="cr-km-card-trend-icon "
                          alt=""
                          style={{ marginRight: "5px" }}
                        />
                      ) : (
                        <img
                          src={ErrorArrowUpIcon}
                          className="cr-km-card-trend-icon "
                          alt=""
                          style={{ marginRight: "5px" }}
                        />
                      )}
                      <Typography variant="h5">
                        {rateChangeState?.previousYearRate}%
                      </Typography>
                    </>
                  )}
                </Box>
                <p style={{ fontSize: "12px", fontWeight: "600" }}>
                  As compared to Last year
                </p>
              </Box>
            )}
          </Box>
        ) : null}
        <p
          style={{
            color: '#202124',
            fontSize: '14px',
            fontWeight: '400',
            textAlign: 'left',
          }}
        >
          {description}
        </p>
      </Box>
      
    </Box>
  );
}


const DEFAULT_ICON = <svg xmlns="http://www.w3.org/2000/svg" width="36.516" height="36.515" viewBox="0 0 36.516 36.515">
  <g id="verify" transform="translate(36.365 36.365) rotate(180)">
    <path id="Path_120186" dataname="Path 120186" d="M9.808,0A9.808,9.808,0,1,1,0,9.808,9.82,9.82,0,0,1,9.808,0Zm0,18.107a8.3,8.3,0,1,0-8.3-8.3A8.309,8.309,0,0,0,9.808,18.107Z" transform="translate(0 0)" fill="#fff" stroke="#fff" strokeWidth="0.3" />
    <path id="Path_120187" dataname="Path 120187" d="M6.036,0A.763.763,0,0,1,6.57.22L9.588,3.238A.754.754,0,0,1,8.521,4.305L6.073,1.858,1.323,7.287A.754.754,0,0,1,.187,6.294L5.468.258A.76.76,0,0,1,6.011,0h.024Z" transform="translate(5.282 6.036)" fill="#fff" stroke="#fff" strokeWidth="0.3" />
    <path id="Path_120188" dataname="Path 120188" d="M.754,0H11.317A3.777,3.777,0,0,1,15.09,3.772V23.389a3.777,3.777,0,0,1-3.772,3.772H8.3a.754.754,0,1,1,0-1.509h3.018a2.266,2.266,0,0,0,2.263-2.263V3.772a2.266,2.266,0,0,0-2.263-2.263H.754A.754.754,0,0,1,.754,0Z" transform="translate(21.125 4.527)" fill="#fff" stroke="#fff" strokeWidth="0.3" />
    <path id="Path_120189" dataname="Path 120189" d="M.754,0a.755.755,0,0,1,.754.754V5.281A2.266,2.266,0,0,0,3.772,7.545H6.79a.754.754,0,1,1,0,1.509H3.772A3.777,3.777,0,0,1,0,5.281V.754A.755.755,0,0,1,.754,0Z" transform="translate(10.563 22.634)" fill="#fff" stroke="#fff" strokeWidth="0.3" />
    <path id="Path_120190" dataname="Path 120190" d="M2.263,0h9.054a2.266,2.266,0,0,1,2.263,2.263V5.281a.755.755,0,0,1-.754.754H10.487a3.773,3.773,0,0,1-7.394,0H.754A.755.755,0,0,1,0,5.281V2.263A2.266,2.266,0,0,1,2.263,0Zm9.808,4.527V2.263a.756.756,0,0,0-.754-.754H2.263a.756.756,0,0,0-.754.754V4.527H3.772a.755.755,0,0,1,.754.754,2.263,2.263,0,0,0,4.527,0,.755.755,0,0,1,.754-.754Z" transform="translate(16.599 27.161)" fill="#fff" stroke="#fff" strokeWidth="0.3" />
    <path id="Path_120191" dataname="Path 120191" d="M.754,0h15.09a.754.754,0,1,1,0,1.509H.754A.754.754,0,1,1,.754,0Z" transform="translate(15.09 22.634)" fill="#fff" stroke="#fff" strokeWidth="0.3" />
    <path id="Path_120192" dataname="Path 120192" d="M.754,0H11.317a.754.754,0,0,1,0,1.509H.754A.754.754,0,1,1,.754,0Z" transform="translate(19.616 18.107)" fill="#fff" stroke="#fff" strokeWidth="0.3" />
    <path id="Path_120193" dataname="Path 120193" d="M.754,0H8.3a.754.754,0,0,1,0,1.509H.754A.754.754,0,1,1,.754,0Z" transform="translate(22.634 13.581)" fill="#fff" stroke="#fff" strokeWidth="0.3" />
  </g>
</svg>
const DEFAULT_ICON_2 = <svg xmlns="http://www.w3.org/2000/svg" width="26.477" height="26.771" viewBox="0 0 26.477 26.771">
  <g id="Assign_to_other" dataname="Assign to other" transform="translate(0.301 0.3)">
    <g id="Group_190724" dataname="Group 190724" transform="translate(0 0)">
      <path id="approval" d="M10.963,25.088a1.02,1.02,0,0,1-1.02,1.02H4.079A4.084,4.084,0,0,1,0,22.028V4.079A4.084,4.084,0,0,1,4.079,0H16.618A4.084,4.084,0,0,1,20.7,4.079v8.24a1.02,1.02,0,1,1-2.04,0V4.079a2.042,2.042,0,0,0-2.04-2.04H4.079a2.042,2.042,0,0,0-2.04,2.04V22.028a2.042,2.042,0,0,0,2.04,2.04H9.943A1.02,1.02,0,0,1,10.963,25.088ZM15.6,6.119H5.094a1.02,1.02,0,1,0,0,2.04H15.6a1.02,1.02,0,1,0,0-2.04Zm1.02,5.1A1.02,1.02,0,0,0,15.6,10.2H5.094a1.02,1.02,0,0,0,0,2.04H15.6A1.02,1.02,0,0,0,16.618,11.218ZM5.094,14.278a1.02,1.02,0,1,0,0,2.04h6.38a1.02,1.02,0,1,0,0-2.04Zm0,0" transform="translate(0)" fill="#eb5b28" stroke="#fff" strokeWidth="0.6" />
      <path id="Edit" d="M9.734,0,9.72,0a.728.728,0,0,0-.3.068L9.407.077A.66.66,0,0,0,9.29.168a.586.586,0,0,0-.076.049L7.5,1.93h0L.648,8.74h0L.59,8.8a.6.6,0,0,0-.034.05A.681.681,0,0,0,.45,9a.742.742,0,0,0-.037.114.747.747,0,0,0-.035.108L0,12.3a.714.714,0,0,0,.007.1A.758.758,0,0,0,0,12.5c0,.018.013.032.017.05a.7.7,0,0,0,.036.106.694.694,0,0,0,.075.147.743.743,0,0,0,.061.075.705.705,0,0,0,.132.114.677.677,0,0,0,.077.046.705.705,0,0,0,.182.061c.018,0,.033.015.052.018l.064,0c.009,0,.015,0,.024,0a.66.66,0,0,0,.106-.008l3.027-.443a.7.7,0,0,0,.077-.028.725.725,0,0,0,.136-.049.717.717,0,0,0,.131-.087.643.643,0,0,0,.062-.041l8.644-8.591a.729.729,0,0,0,0-1.034L10.26.206A.716.716,0,0,0,9.749,0,.141.141,0,0,0,9.734,0ZM9.047,2.45l.689-.686,1.6,1.594-.688.688Zm-5.3,8.471L2.137,9.316,8.013,3.479,9.62,5.087Z" transform="matrix(0.996, 0.087, -0.087, 0.996, 13.104, 12.257)" fill="#eb5b28" />
    </g>
  </g>
</svg>
  ;
const DEFAULT_COLOR = '#F45E29';
const DEFAULT_TITLE = 88;


function ArrowNumberChart({
  icon = DEFAULT_ICON,
  color = DEFAULT_COLOR,
  title = DEFAULT_TITLE,
  reportDisplayName = '',
  rateChangeState,
  isTitleNoDataText = false
}) {
  return (
    <Box
      className={`arrownumber_carousel_card_wrapper ` + (isTitleNoDataText ? `title-is-noDataText` : ``)}
      sx={{
        overflow: 'hidden',
        // paddingLeft: '5px',
        // paddingRight: '5px',
        // border: '1px solid #85888A',

        width: '209px',
        height: '240px',
        // margin: '0 auto',
        padding: '0',
        paddingRight: '5px !important',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: '100%',
          height: '188px',
          paddingLeft: '5px'
        }}
      >
        <Box
          sx={{
            borderStyle: 'dashed',
            borderWidth: '2px',
            width: '200px',
            height: '50%',
            borderBottom: 'none',
            borderRadius: '10px 10px 0 0',
            borderColor: '#85888A',
          }}
        ></Box>
        <Box
          sx={{
            borderStyle: 'solid',
            borderWidth: '2px',
            width: '200px',
            height: '50%',
            borderTop: 'none',
            borderRadius: '0 0 10px 10px',
            borderColor: color,
          }}
        ></Box>
        <Paper
          sx={{
            width: '80%',
            height: '80%',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column'

          }}
          elevation={5}
        >
          <Box
            sx={{
              width: '76px',
              height: '76px',
              backgroundColor: color,
              borderBottomLeftRadius: '999px',
              borderBottomRightRadius: '999px',

              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: "36px",
                height: "36px",
                overflow: 'hidden',
                color: '#fff',
              }}
            >
              {icon ? <img style={{ width: '100%', height: '100%', overflow: 'hidden', fill: 'red' }} src={icon} alt="icon" /> : null}

            </Box>
          </Box>
          <Box
            className="arrownumber_carousel_card_wrapper-title"
            sx={{
              width: '100%',
              height: '60%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',

              textAlign: 'center',
              padding: '0 10px',
              font: 'normal normal 600 22px/30px Open Sans',
              letterSpacing: '0px',
              color: '#202124',
              opacity: 1,
            }}
          >
            {
              rateChangeState
                ? (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    {/* <Typography>{rateChangeState.currNum}</Typography> */}
                    {rateChangeState.currNum}
                    <Box>
                      <Box className='cr-km-trend-periodicity-box'>
                        {
                          rateChangeState?.isNoChange
                            ? NO_RATE_CHANGE_SVG
                            : (
                              <>
                                {
                                  (rateChangeState.numberTrend === 'up') ?
                                    <img src={SuccessArrowDownIcon} className='cr-km-card-trend-icon ' alt="" />
                                    : <img src={ErrorArrowUpIcon} className='cr-km-card-trend-icon ' alt="" />
                                }
                                <Box className='cr-km-card-trend-value ' >
                                  <Typography variant="body2" className={rateChangeState.numberTrend === "up" ? "trend-icon-success" : "trend-icon-error"}>{rateChangeState.rate + '%'}</Typography>
                                </Box>
                              </>
                            )
                        }
                      </Box>
                      <Box className='cr-km-trend-periodicity'>{_.startCase(rateChangeState.rateChangeProps?.periodicity)}</Box>
                    </Box>
                  </Box>
                )
                : isTitleNoDataText
                    ? (title?.length > 20) // 20 char spans over 2 lines
                        ? <CustomTooltip
                            title={title}
                            placement="bottom"
                            PopperProps={{
                                className: 're-tooltip-number-chart'
                            }}
                          >{title}</CustomTooltip>
                        : title
                    : title
            }
          </Box>
        </Paper>
        <Box
          sx={{
            position: 'absolute',
            bottom: '-14px',
            left: '50%',
            transform: 'translate(-50%, 0)',
            width: '0px',
            height: '0px',
            borderLeft: '14px solid transparent',
            borderRight: '14px solid transparent',
            borderTop: '14px solid',
            borderTopColor: color,
          }}
        ></Box>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '0px',
            transform: 'translate(0, -50%)',
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: color,
          }}
        ></Box>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            right: '-5px',
            transform: 'translate(0, -50%)',
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: color,
          }}
        ></Box>
      </Box >
      <p style={{
        textAlign: 'center',
        font: 'normal normal normal 14px/20px Open Sans',
        letterSpacing: '0px',
        color: '#202124',
        opacity: '1'
      }}>{reportDisplayName}</p>
    </Box>
  )
}

function BackgroundImageNumberChart({
  icon = DEFAULT_ICON_2,
  backgroundColor = DEFAULT_COLOR,
  backgroundImage,
  title = 'No. of papper assigned',
  subTitle = DEFAULT_TITLE,
  rateChangeState
}) {
  if (!(backgroundImage || backgroundColor)) {
    throw new Error('A backgroundColor or a backgroundImage must be provided!')
  }
  return (
    <Box sx={{
      margin: 'auto',
      width: '225px',
      height: '150px',
      backgroundColor: backgroundColor,
      backgroundImage: `url(${backgroundImage})`,
      color: 'white',
      borderRadius: '10px',
      display: 'flex',
      flexDirection: 'column',
      padding: '20px'
    }}
    >
      <Box sx={{
        width: '40px',
        height: '40px',
        minHeight: '40px',
        minWidth: '40px',
        backgroundColor: 'white',
        overflow: 'hidden',
        borderRadius: '8px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
        marginBottom: '14px',
        color: backgroundColor,
        padding: '5px'
      }}
      >
        {icon ? <img style={{ width: '100%', height: '100%', overflow: 'hidden' }} src={icon} alt="icon" /> : null}
      </Box>
      <Box sx={{
        textAlign: 'left',
        font: 'normal normal normal 16px Open Sans',
        lineHeight: '22px',
        letterSpacing: '0px',
        color: '#FFFFFF'
      }}>
        {title}
      </Box>
      <Box sx={{
        textAlign: 'left',
        font: 'normal normal 600 22px Open Sans',
        lineHeight: '34px',
        letterSpacing: '0px',
        color: '#FFFFFF'
      }}>
        {
          rateChangeState
            ? (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
              }}>
                {/* <Typography>{rateChangeState.currNum}</Typography> */}
                {rateChangeState.currNum}
                <Box>
                  <Box className='cr-km-trend-periodicity-box'>
                    {
                      rateChangeState?.isNoChange
                        ? NO_RATE_CHANGE_SVG
                        : (
                          <>
                            {
                              (rateChangeState.numberTrend === 'up') ?
                                <img src={SuccessArrowDownIcon} className='cr-km-card-trend-icon ' alt="" />
                                : <img src={ErrorArrowUpIcon} className='cr-km-card-trend-icon ' alt="" />
                            }
                            <Box className='cr-km-card-trend-value ' >
                              <Typography variant="body2" className={rateChangeState.numberTrend === "up" ? "trend-icon-success" : "trend-icon-error"}>{rateChangeState.rate + '%'}</Typography>
                            </Box>
                          </>
                        )
                    }
                  </Box>
                  <Box className='cr-km-trend-periodicity'>{_.startCase(rateChangeState.rateChangeProps?.periodicity)}</Box>
                </Box>
              </Box>
            )
            : subTitle
        }
      </Box>
    </Box >
  )
}

function StrogWeakNumberChart({
  topic = '',
  title = 'title',
  description = 'description',
  icon
}) {
  return (
    <Paper sx={{
      margin: 'auto',
      // height: '196px',
      // width: '542px',
      height: '100%',
      width: '100%',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
    }}
    >
      <Box sx={{
        margin: 'auto',
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        color: '#202124',
        marginBottom: '17px',
      }}>
        <Box
          sx={{
            width: '60px',
            height: '60px',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '9999px',
            background: "transparent linear-gradient(140deg, #F45E29 0%, #EEAF56 73%, #EEAF56 100%) 0% 0% no-repeat padding-box"
          }}
        >
          <Box
            sx={{
              width: '30px',
              height: '30px',
              overflow: 'hidden',
            }}
          >
            {icon ? <img style={{
              width: '100%',
              height: '100%',
            }} alt="strog weak icon" src={icon} /> : DEFAULT_ICON}
          </Box>
        </Box>
        <Typography variant='h3' sx={{ marginLeft: '15px' }}>{title}</Typography>
      </Box>
      <Divider />
      <Box sx={{
        minHeight: '61px',
        textAlign: 'left',
        font: 'normal normal normal 14px/20px Open Sans',
        letterSpacing: '0px',
        color: '#202124',
        marginTop: '20px',
      }}>
        {description}
        {
          <span style={{
            textAlign: 'left',
            textDecoration: 'underline',
            font: 'normal normal 600 14px/20px Open Sans',
            letterSpacing: '0px',
            color: '#4482FF',
          }}>{topic}</span>
        }

      </Box>
    </Paper >
  )
}

function getCalculateNumber({ resultSet, fieldFormat, settings, noDataText, query }) {
  let totalNum = 0;
  let isPercent = false;
  let groupTotalNum = [];
  let groupConcatBy = ''

  const rawData = resultSet?.queryType === 'blendingQuery' ? resultSet?.decompose().map(r => r.rawData()?.[0]) : resultSet.rawData();
  if (rawData.length === 0) {
    return noDataText;
  }
  const field = query.measures?.[0] || query.dimensions?.[0];
  totalNum = rawData?.[0]?.[field] || 0;

  if (settings?.groupBySettings?.length) {
    const groupBySetting = settings?.groupBySettings?.find(setting => setting?.concatAt === field);
    if (groupBySetting) {
      groupConcatBy = groupBySetting?.concatBy;
      groupBySetting.concatWith?.forEach(concatWith => {
        groupTotalNum.push(rawData?.[0]?.[concatWith] || 0)
      })
    }
  }

  // if (settings?.groupBySettings?.length) {
  //   let data = resultSet.series()?.[0];
  //   isPercent = data?.key && fieldFormat?.[data?.key] === 'percent' ? true : false;
  //   totalNum = data?.series?.map(obj => obj?.value || 0)?.reduce((a, b) => a + b, 0)
  //   const groupBySetting = settings?.groupBySettings?.find(setting => setting?.concatAt === data?.key);

  //   if (groupBySetting) {
  //     groupConcatBy = groupBySetting?.concatBy;
  //     groupBySetting.concatWith?.forEach(concatWith => {
  //       let concatData = resultSet.series()?.find(obj => obj?.key === concatWith);
  //       groupTotalNum.push(concatData?.series?.map(obj => obj?.value || 0)?.reduce((a, b) => a + b, 0))
  //     })
  //   }
  // } else {
  //   let data = resultSet.series()?.[0];
  //   isPercent = data?.key && fieldFormat?.[data?.key] === 'percent' ? true : false;
  //   totalNum = data?.series?.map(obj => obj?.value || 0)?.reduce((a, b) => a + b, 0)
  // }

  if (totalNum === 0) {
    return noDataText
  }

  if (settings?.conditionalFormat && settings?.conditionalFormat?.length && settings?.conditionalFormat[0]?.decimalPrecision) {
    totalNum = Number(totalNum).toFixed(settings?.conditionalFormat[0]?.decimalPrecision);
    if (groupTotalNum.length) {
      groupTotalNum = groupTotalNum.map(num => Number(num).toFixed(settings?.conditionalFormat[0]?.decimalPrecision))
    }
  }
  //console.log(settings?.dataFormat)
  if (settings?.dataFormat?.currencyFormat) {
    totalNum = formatCurrency(totalNum,settings?.dataFormat?.currencyFormat)
    if (groupTotalNum.length) {
      groupTotalNum = groupTotalNum.map(num => formatCurrency(num,settings?.dataFormat?.currencyFormat))
    }
  } else if (settings?.dataFormat?.timeFormat) {
    totalNum = formatNumberTimeField(totalNum, settings?.dataFormat?.timeFormat, true)
    if (groupTotalNum.length) {
      groupTotalNum = groupTotalNum.map(num => formatNumberTimeField(num, settings?.dataFormat?.timeFormat, true))
    }
  } else if (settings?.dataFormat?.format === 'percentage') {
    totalNum = `${totalNum} %`
    if (groupTotalNum.length) {
      groupTotalNum = groupTotalNum.map(num => `${num} %`)
    }
  } else {
    if(!isPercent) totalNum = FormatToShortNumber(totalNum);
    totalNum = `${totalNum}${isPercent ? " %" : ""}`
    if (groupTotalNum.length) {
      groupTotalNum = groupTotalNum.map(num => `${num}${isPercent ? " %" : ""}`)
    }
  }

  return `${totalNum}${groupTotalNum.length ? `${groupConcatBy}${groupTotalNum.join(` ${groupConcatBy} `)}` : ''}`
}


const handleReportHyperlink = async (hyperlinkData, record, setHyperlinkReportData, query) => {
  const { hyperlinkFilters, hyperlinkReportId, hyperlinkType } = hyperlinkData;
  //console.log(query)

  getReportById({ reportId: hyperlinkReportId?._id }).then(async result => {
    let report = result?.result
    if (report) {
      let currentDataSetName = null;
      let hyperlinkDatasetName = null;

      if (query && query?.dimensions.length) {
        currentDataSetName = query?.dimensions[0].split('.')[0]
      }
      if (query && query?.measures.length) {
        currentDataSetName = query?.measures[0].split('.')[0]
      }
      else if (query && query?.timeDimensions.length) {
        currentDataSetName = query.timeDimensions[0].dimension?.split('.')[0]
      }
      if (report && report?.dimensions.length) {
        hyperlinkDatasetName = report?.dimensions[0]?.fieldName.split('.')[0]
      }
      if (report && report?.measures.length) {
        hyperlinkDatasetName = report?.measures[0]?.fieldName.split('.')[0]
      }
      else if (report && report?.timeDimensions.length) {
        hyperlinkDatasetName = report.timeDimensions[0].dimension?.split('.')[0]
      }

      const combindedFilter = [...report.reportFilters]
      const parentFilters = hyperlinkFilters.length > 0 ? [
        ...query?.filters,
        ...hyperlinkFilters?.map(measure => {
          return {
            member: hyperlinkDatasetName + '.' + measure,
            operator: 'equals',
            values: [record[currentDataSetName + '.' + measure]]
          }
        })
      ]: [...query?.filters]
      //console.log(parentFilters)
      if (parentFilters.length > 0) {
        const measureListReponse = await getMeasuresList({ dataSetId: report.dataSetId?._id });
        const measureList = measureListReponse?.result;
        if (measureList.length > 0) {
          const measureListName = measureList.map(measure => measure?.name);
          //console.log(measureListName)
          parentFilters.forEach((filter, index) => {
            const measureName = filter?.member?.split('.')[1];
            if (!measureListName.includes(measureName)) return;
            const filterExistInCurrentReportIndex = report?.reportFilters?.findIndex(reportFilter => reportFilter?.member?.split('.')[1] === measureName && reportFilter?.operator === filter?.operator);
            if (filterExistInCurrentReportIndex > -1) {
              combindedFilter[filterExistInCurrentReportIndex].values = filter?.values ? [...new Set([...combindedFilter[filterExistInCurrentReportIndex].values, ...filter?.values])] : [...new Set([...combindedFilter[filterExistInCurrentReportIndex].values])]
              return;
            }
            combindedFilter.push({
              member: hyperlinkDatasetName + '.' + measureName,
              operator: filter?.operator,
              values: filter?.values
            });
          });
        }
      }
      report.reportFilters = combindedFilter;
      //console.log(combindedFilter)
    }
    //console.log(report)
    //setHyperlinkType(hyperlinkType)
    setHyperlinkReportData(report)
  })
}

const handleExternalReportHyperlink = (query, record, extData, url, type, setHyperlinkExtUrl, setHyperlinkModal, setUserInfo, setExtReportType,targetType = null) => {
  const urlRegex = /[(http(s)?):\/\/(www\.)?a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/ig
  if (!urlRegex.test(url)) {
    toast.error('URL is not valid');
    return false
  }
  let hyperlinkUrl = url
  let currrentDataSetName = null;
  if (query && query?.dimensions.length) {
    currrentDataSetName = query?.dimensions[0].split('.')[0]
  }
  if (query && query?.measures.length) {
    currrentDataSetName = query?.measures[0].split('.')[0]
  }
  else if (query && query?.timeDimensions.length) {
    currrentDataSetName = query.timeDimensions[0].dimension?.split('.')[0]
  }

  if(!targetType){
    setExtReportType("")
    setUserInfo({})
  }
  

  let parsedUrl = new URL(hyperlinkUrl)

    if (type === "params") {
      if (extData && extData.length) {
        extData.forEach(param => {
          const value = record[currrentDataSetName + '.' + param]
          hyperlinkUrl += `/${value}`
        })
        if(targetType){
          return parsedUrl
        }else{
          setHyperlinkModal(true)
          setHyperlinkExtUrl(hyperlinkUrl)
        }        
        
      }
      else {
        toast.error('URL Params is required')
        return false
      }
    }
    else if (type === "qryParams") {
      if (extData && extData.length && Object.values(extData[0]).length && !Object.values(extData[0]).includes('')) {
        extData.forEach((qry, index) => {
          const { name, field } = qry;

          if (name && field) {
            const value = record[currrentDataSetName + '.' + field]

            if (extData.length === 1) {
              hyperlinkUrl += `?${name}=${value}`
            }
            else {
              if (index === 0) {
                hyperlinkUrl += `?${name}=${value}&`
              }
              else if (index > 0 && index < extData.length - 1) {
                hyperlinkUrl += `${name}=${value}&`
              }
              else {
                hyperlinkUrl += `${name}=${value}`
              }
            }

          }
        })
        if(targetType){
          return parsedUrl
        }else{
          setHyperlinkModal(true)
          setHyperlinkExtUrl(hyperlinkUrl)
        }
      }
      else {
        toast.error('URL Query Params is required')
        return false
      }
    }
    if(targetType){
      return parsedUrl
    }else{
      setHyperlinkModal(true)
      setHyperlinkExtUrl(hyperlinkUrl)
    }
}